<template>
	<Head title="Atualizar foto de matrícula" />

	<div class="card">
		<div class="card-header">
			<h3 class="card-title">
				<i class="fa fa-camera me-3"></i>
				Atualizar foto de matrícula
			</h3>
		</div>

		<div class="card-body">
			<div class="alert alert-success" v-if="success" id="success_message">
				<h4 class="text-success mb-1">Foto atualizada!</h4>
				<span>{{ success_message }}</span>
				<div>
					<a :href="props.dashboard_url" class="btn btn-primary btn-sm mt-3">
						Voltar para o início
					</a>
				</div>
			</div>

			<div class="row" v-else>
				<div class="col-12 col-md-6">
					<ul>
						<li>
							O navegador precisa ter permissão para acessar a
							câmera.
						</li>
						<li>Certifique-se que somente você apareça na foto.</li>
						<li>
							Mantenha a
							<u style="color: #022d62">CABEÇA RETA</u> e o
							<u style="color: #022d62">OLHAR FIXO</u> para a
							câmera.
						</li>
						<li>
							Retire qualquer acessório que oculte
							<u style="color: #022d62">PARCIAL</u> ou
							<u style="color: #022d62">TOTALMENTE</u> seu
							<u style="color: #022d62">ROSTO</u>.
						</li>
					</ul>

					<div
						class="alert alert-danger mt-5"
						v-if="error_message"
						id="photo_error">
						{{ error_message }}
					</div>
				</div>

				<div class="col-12 col-md-6 d-flex flex-column align-items-center">
					<div class="box-camera text-center">
						<div id="foto" class="rounded" v-if="photo">
							<img
								style="width: 250px; height: 250px"
								:src="'data:image/jpeg;base64,' + photo" />
						</div>
						<div id="camera" class="rounded" v-else>
							<img
								v-if="mock_student_photo"
								class="img-photo"
								:src="
									'data:image/jpeg;base64,' +
									props.mock_photo_b64
								" />
						</div>
					</div>

					<div class="d-flex flex-wrap justify-content-center mt-5">
						<button
							v-if="!photo"
							@click="capturePhoto"
							id="camera-take-snapshot"
							class="btn btn-primary m-2"
							type="button">
							<i class="fa fa-camera"></i> Capturar foto
						</button>
						<template v-else>
							<button
								@click="retryPhoto"
								id="camera-retry-snapshot"
								class="btn btn-warning m-2"
								type="button"
								:disabled="saving">
								<i class="fa fa-sync"></i> Tirar outra
							</button>
							<button
								@click="submit"
								id="submit-photo"
								class="btn btn-success m-2"
								type="button"
								:disabled="saving">
								<span
									v-if="saving"
									class="spinner-border spinner-border-sm me-2"
									role="status"
									aria-hidden="true"></span>
								Salvar foto
							</button>
						</template>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
	import { onMounted, ref } from 'vue';
	import { Head, usePage } from '@inertiajs/vue3';
	import axios from 'axios';

	const props = defineProps({
		store_url: { type: String, required: true },
		dashboard_url: { type: String, required: true },
		mock_photo_b64: { type: String, required: false, default: '' },
	});

	const photo = ref(null as string | null);
	const error_message = ref('');
	const success_message = ref('');
	const success = ref(false);
	const saving = ref(false);

	const mock_student_photo = usePage().props?.mock_user_photo as boolean;

	const webcam_settings = {
		width: 400,
		height: 300,
		image_format: 'jpeg',
		jpeg_quality: 100,
	};

	function attachWebcam() {
		window.Webcam.set(webcam_settings);
		window.Webcam.attach('#camera');
	}

	function capturePhoto() {
		error_message.value = '';

		if (mock_student_photo) {
			photo.value = props.mock_photo_b64;
			return;
		}

		takeSnapshot().then((response) => {
			photo.value = response.base64;
		});
	}

	function retryPhoto() {
		photo.value = null;
		error_message.value = '';

		if (mock_student_photo) {
			return;
		}

		resetWebcam().then(() => attachWebcam());
	}

	function submit() {
		saving.value = true;
		error_message.value = '';

		axios
			.post(props.store_url, { photo: photo.value })
			.then((response) => {
				success_message.value = response.data.message;
				success.value = true;
			})
			.catch((error) => {
				error_message.value =
					error.response?.data?.message ??
					'Não foi possível salvar a foto. Tente novamente.';
				// A foto reprovada volta para a câmera: manter a imagem na tela
				// só levaria o aluno a salvar a mesma foto de novo.
				photo.value = null;

				if (!mock_student_photo) {
					resetWebcam().then(() => attachWebcam());
				}
			})
			.finally(() => {
				saving.value = false;
			});
	}

	onMounted(() => {
		if (!mock_student_photo) {
			attachWebcam();
		}
	});
</script>

<style scoped>
	#camera {
		width: 100% !important;
		height: 100% !important;
		overflow: hidden;
		display: flex;
		justify-content: center;
		border-radius: 0.475rem;
	}

	.img-photo {
		width: 60%;
		height: 85%;
		border-radius: 50%;
	}

	.box-camera {
		width: 250px;
		height: 250px;
		border-radius: 50%;
		box-shadow:
			rgb(255, 102, 42) 0px 0px 0px 5px,
			rgb(255, 255, 255) 0px 0px 0px 0px;
		overflow: hidden;
		align-items: center;
	}
</style>
